/**
 *    API SERVICE
 *    used by core services, no feature should be calling the api directly
 */
import axios, { AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { saveAs } from 'file-saver';
import { ngDelegate, ngContext } from './ng.delegate';
import { utilSvc, validSvc } from '../services/exports';
import sessionRepo from '../repo/sessionRepo';
import apiErrorHandler from './api.errors';
import { ErrorVM, HttpErrorIgnore, HttpErrorUpsert, HttpImportResult, HttpUpsert200 } from '../../../global';

type HttpEntity = {
    Entity: object;
};
export type AxiosEntity = AxiosResponse & HttpEntity;

// Represents a single user preference node (e.g., TABLEVIEW, DASHBOARD, etc.)
export type UserProfilePref = {
    Type: string;
    Value?: string;
    // ...other fields as needed
};

// DEFINITION
const apiSvc = {
    getEntity,
    getEntityList,
    postGetBinary,
    post,
    postModel,
    postFile,
};

async function getEntity(purl: string, data?: object): Promise<object> {
    const r: AxiosEntity = await post(purl, data);
    if (!!!r.Entity) {
        ngDelegate.error(
            'API response incorrect',
            'apiSvc.getEntity',
            '200',
            'r.data.Entity object required for purl: ' + purl,
        );
        throw utilSvc.http.ignore(undefined, 'getEntity 200');
    }
    return r.Entity;
}

async function getEntityList<TListItem>(purl: string, data?: object): Promise<TListItem[]> {
    const r: AxiosEntity = await post(purl, data);
    if (!Array.isArray(r.Entity)) {
        ngDelegate.error(
            'API response incorrect',
            'apiSvc.getEntityList',
            '200',
            'r.data.Entity is not an array purl: ' + purl,
        );
        throw utilSvc.http.ignore(undefined, 'api.getEntityList 200');
    }
    return r.Entity;
}

async function post(
    purl: string,
    data?: object,
    isDebug: boolean = false,
    isBinary: boolean = false,
    expectTimeout: boolean = false,
    isMultiPartFile: boolean = false,
): Promise<any> {
    const ctx = ngContext();

    try {
        ngDelegate.pongTimer('api: ' + purl); // angular keep alive

        data = data || {};
        const r: AxiosResponse = await axios.post(
            ctx.appConfig.apiPath + purl,
            data,
            authHeader(ctx.sessionToken, isBinary, isMultiPartFile),
        );
        const errEvent: ErrorVM | null = checkResponse(r, purl);
        if (!!errEvent) {
            // 200 response is malformed
            ngDelegate.warn(
                'apiSvc.post, Api 200 response error: ' + errEvent.msg,
                errEvent.stack,
                errEvent.status,
                errEvent.detail,
            );
            if (isDebug) {
                // build debug err object response
                const resp = {
                    status: r.status,
                    data: r.data || {},
                };
                resp.data.isDebug = true;
                resp.data.errEvent = errEvent;
                throw { response: resp }; // axios error return is wrapped in response
            } else {
                throw utilSvc.http.ignore(undefined, 'apipost 200');
            }
        }

        // api log
        if (!isDebug && ctx.apiLog) {
            logRequest(purl, data, r);
        }

        return isDebug ? r : r.data;
    } catch (err: any) {
        console.log('POST ERR: ');

        // already handled
        if (utilSvc.http.isIgnore(err)) {
            throw utilSvc.http.ignore(err, 'api.post err');
        }

        // axios default
        err = err || {};
        const errResponse = err.response || {
            status: 502,
            data: {},
        };

        // debug exit
        if (isDebug && !!errResponse.data.isDebug) {
            throw errResponse;
        }

        // api log
        if (!isDebug && ctx.apiLog) {
            logRequest(purl, data, errResponse);
        }

        // expect 503 timeouts
        if (expectTimeout && errResponse.status === 503) {
            errResponse.status = 409;
            errResponse.timeout = true;
            throw errResponse;
        }

        const errEvent = apiErrorHandler(err.response, purl);
        if (!!errEvent) {
            if (postShowErrMsg(errEvent, !!ngContext().User)) {
                console.log('POST ERR HANDLE: ' + errEvent);
                ngDelegate.warn(errEvent.msg, errEvent.stack, errEvent.status, errEvent.detail);
            }
            throw utilSvc.http.ignore(undefined, 'api.post err');
        }

        throw errResponse;
    }
}

async function postModel(
    purl: string,
    m: object,
    return422: boolean = true,
    return409: boolean = false,
): Promise<HttpUpsert200 | HttpErrorUpsert> {
    try {
        const r = await post(purl, m);

        // bad model post response format (must have status 200 and Entity.Id defined)
        if (!validSvc.httpUpsert200Resp(r)) {
            ngDelegate.warn('apiSvc.postModel, Api 200 response format', purl, '200', utilSvc.http.safeResponse(r));
            throw utilSvc.http.ignore(undefined, 'postModel 200');
        }

        // return ok status
        const ok = r as HttpUpsert200;
        ok.status = 200;
        return ok;
    } catch (err: any) {
        console.log('POSTMODEL ERR:', err);
        // already handled
        if (utilSvc.http.isIgnore(err)) {
            throw utilSvc.http.ignore(err, 'api.postModel err') as HttpErrorIgnore;
        }

        if ((return409 && err.status == 409) || (return422 && err.status == 422)) {
            // repo or store is handling, return it as-is
            throw err as HttpErrorUpsert;
        }

        // all other errors are not editing related and unexpected
        ngDelegate.warn('Unexpected editing api error', purl, err.status.toString(), utilSvc.http.safeResponse(err));
        throw utilSvc.http.ignore(undefined, 'api.postModel unexpected') as HttpErrorIgnore;
    }
}

async function postGetBinary(
    purl: string,
    m: object,
    fileName: string,
    fileType: BinaryFileType,
): Promise<undefined | HttpErrorIgnore> {
    try {
        const r = await post(purl, m, false, true);
        const newBlob = new Blob([r], { type: binaryFileType(fileType) });
        saveAs(newBlob, utilSvc.regex.cleanFilename(fileName) + binaryFileExtension(fileType));
        return;
    } catch (err: any) {
        // already handled
        if (utilSvc.http.isIgnore(err)) {
            throw utilSvc.http.ignore(err, 'api.postGetBinary') as HttpErrorIgnore;
        }
        // tbd... timeout errors
        // all other errors unexpected
        ngDelegate.warn(
            'Unexpected binary file error for file: ' + fileName + ', type: ' + fileType,
            purl,
            utilSvc.http.safeResponse(err),
        );
        throw utilSvc.http.ignore(undefined, 'api.postGetBinary unexpected') as HttpErrorIgnore;
    }
}

/**
 *    POST FILE IN MULTI-PART FORM
 *    send Multi-part form, receive JSON response
 *    note: uses FormData object, browser must support this
 */

async function postFile(
    purl: string,
    request: object,
    importFile: Blob,
    responseFileType: BinaryFileType = BinaryFileType.None,
    responseFileName: string = '',
    expectTimeout: boolean = false,
): Promise<HttpImportResult | HttpErrorUpsert> {
    try {
        // check params
        if (!request || !importFile) {
            throw new Error('apiSvc.postFile, api request and import file are required');
        }
        const hasFileResponse = responseFileType != BinaryFileType.None;
        if (hasFileResponse && !validSvc.isText(responseFileName)) {
            throw new Error('apiSvc.postFile, response filename is required for a file response');
        }

        // build multi-part form data
        var fd = new FormData();
        fd.append('context', JSON.stringify(request)); // stringify JSON into one "context" property
        fd.append('file', importFile as Blob);

        const r = await post(purl, fd, false, hasFileResponse, expectTimeout, true);
        r.status = 200;

        // file response
        if (hasFileResponse) {
            if (!(r instanceof ArrayBuffer)) {
                ngDelegate.warn(
                    'apiSvc.postFile, Api 200 response has no array buffer',
                    purl,
                    '200',
                    utilSvc.http.safeResponse(r),
                );
                throw utilSvc.http.ignore(undefined, 'postFile 200');
            }
            const newBlob = new Blob([r], { type: binaryFileType(responseFileType) });
            saveAs(newBlob, utilSvc.regex.cleanFilename(responseFileName) + binaryFileExtension(responseFileType));
        }

        return parseImportFileResponse(r);
    } catch (err: any) {
        // already handled
        if (utilSvc.http.isIgnore(err)) {
            throw utilSvc.http.ignore(err, 'api.postGetBinary') as HttpErrorIgnore;
        }
        err = err || {};

        // return 409 as-is
        // note: timeout 503 is converted to 409 in post()
        if (err.status == 409) {
            throw err as HttpErrorUpsert;
        }

        // parse return to import result
        const result = parseImportFileResponse(err);
        if (result.status == 200 || result.status == 422) {
            return result;
        }

        // anything else is unexpected
        ngDelegate.warn(
            'Unexpected binary file error for : ' + purl + ', resp type: ' + responseFileType,
            purl,
            utilSvc.http.safeResponse(err),
        );
        throw utilSvc.http.ignore(undefined, 'api.postFile unexpected') as HttpErrorIgnore;
    }
}

/**
 *    HELPERS
 */

function authHeader(token: string, isBinary: boolean = false, isMultiPartFile: boolean = false): AxiosRequestConfig {
    const headers: RawAxiosRequestHeaders = { Authorization: token };
    const config: AxiosRequestConfig = { headers };

    if (isMultiPartFile) {
        headers['enctype'] = 'multipart/form-data';
    }
    if (isBinary) {
        config.responseType = 'arraybuffer';
        headers['Content-Type'] = 'blob';
    }
    return config;
}

function checkResponse(r: any, purl: string): ErrorVM | null {
    // verify the format of a successful response object
    if (!!!r || !validSvc.isInteger(r.status) || !!!r.data) {
        return { msg: 'resp.ok.corrupt', stack: purl, status: '200', detail: utilSvc.http.safeResponse(r) };
    } else {
        return null;
    }
}

function postShowErrMsg(errEvent: ErrorVM, hasUser: boolean): boolean {
    return hasUser && errEvent.code != 'BLOCK' && errEvent.status != '401' && errEvent.status != '423';
}

function cleanPath(dirtyPurl): any {
    // assign purl
    if (!validSvc.isText(dirtyPurl)) {
        alert('cleanPath, purl must be a valid string');
        return null;
    }

    // remove all leading and trailing '/'
    let fix = dirtyPurl.trim();
    while ((fix.length > 1 && fix.substr(0, 1) == '/') || fix.substr(fix.length - 1, 1) == '/') {
        if (fix.substr(0, 1) == '/') {
            fix = fix.substr(1);
        }

        if (fix.substr(fix.length - 1, 1) == '/') {
            fix = fix.substr(0, fix.length - 1);
        }
    }

    return fix;
}

function logRequest(purl: string, data, r): void {
    r = r || {};
    if (!!r.config && !!r.config.headers) {
        r.config.headers.Authorization = '';
    }

    try {
        // resp too big?
        const jsonResp = JSON.stringify(r),
            status = validSvc.isInteger(r.status) ? r.status.toString() : '';

        const logEntry = {
            name: cleanPath(purl),
            json: data,
            response: jsonResp.length < 1000000 ? r : { data: 'response is to big for api log: ' + jsonResp.length },
            type: 'SAVE',
            status: status,
        };

        let log = sessionRepo.getLocalStoreKey('apilog') as object[];
        log = Array.isArray(log) ? log : [];
        log.unshift(logEntry);
        if (log.length > 10) {
            log.pop();
        }

        sessionRepo.setLocalStoreKey('apilog', log);
    } catch (err: any) {
        console.log('apiService.logRequest error: ' + err.message);
    }
}

export enum BinaryFileType {
    None,
    Excel,
    Csv,
    Pdf,
    Zip,
}

export function binaryFileType(fileType: BinaryFileType): string {
    switch (fileType) {
        case BinaryFileType.Excel:
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case BinaryFileType.Csv:
            return 'text/csv';
        case BinaryFileType.Pdf:
            return 'application/pdf';
        case BinaryFileType.Zip:
            return 'application/zip';
        default:
            throw new Error('binaryFileType, unknown file type: ' + fileType);
    }
}

export function binaryFileExtension(fileType: BinaryFileType): string {
    switch (fileType) {
        case BinaryFileType.Excel:
            return '.xlsx';
        case BinaryFileType.Csv:
            return '.csv';
        case BinaryFileType.Pdf:
            return '.pdf';
        case BinaryFileType.Zip:
            return '.zip';
        default:
            throw new Error('binaryFileExtension, unknown file type: ' + fileType);
    }
}

function parseImportFileResponse(r: any): HttpImportResult {
    // all 200 and 422 responses come through here
    // convert status and counts to 1 return result
    r = r || {};
    const ent = r.Entity || {};
    let status = validSvc.isInteger(r.status) ? r.status : 422;
    const processed = validSvc.isInteger(ent.ProcessedCount) ? ent.ProcessedCount : 0,
        received = validSvc.isInteger(ent.ReceivedCount) ? ent.ReceivedCount : 0,
        errorCount = validSvc.isInteger(ent.ErrorCount) ? ent.ErrorCount : 0,
        lineErrors = Array.isArray(ent.RecordErrors) ? ent.RecordErrors : [];

    if (status == 200 && processed < received) {
        // all imports not processed
        status = 422;
    } else if (status == 200 && (errorCount > 0 || lineErrors.length > 0)) {
        // import error
        status = 422;
    }

    return {
        status: status,
        LineErrors: lineErrors,
        Processed: processed,
        Received: received,
        ErrorCount: errorCount,
        DuplicateCount: validSvc.isInteger(ent.DuplicateCount) ? ent.DuplicateCount : 0,
    };

    // TBD... 422 Fields to parse?
    // some api endpoints return the RecordErrors in the 422 FieldErrors array
    // else if(r.status == 422) {
    //     // has Fields?
    //     var fieldErrors = '',
    //         ent = r.data.Entity;
    //     if(Array.isArray(ent.Fields) && ent.Fields.length > 0) {
    //         fieldErrors = ', field errors: ' + ent.Fields.join(', ');
    //     }
}

export default apiSvc;
